// 암산·확률 — 제한 시간 안에 계산. 레벨이 오르면 사칙연산 → 퍼센트 → 주사위·조합 → 기댓값·조건부 → 팟 오즈·베이즈.
// 한 판은 최근 세 레벨의 문제 유형을 섞는다 (익숙한 것만 반복하지 않게).
import { hasBatchim, int, makeRng, pick, shuffle, type Rng } from './rng';

export interface CalcQuestion {
  type: string;
  text: string;
  answer: string;
  /** 있으면 보기 고르기, 없으면 숫자 입력 */
  choices?: string[];
  limitSec: number;
}

type Made = { text: string; answer: string; wrong?: string[] };
interface QType { key: string; level: number; limit: number; make: (rng: Rng) => Made }

export const CALC_COUNT = 10;

function gcd(a: number, b: number): number {
  return b === 0 ? Math.abs(a) : gcd(b, a % b);
}

export function frac(n: number, d: number): string {
  if (n === 0) return '0';
  const g = gcd(n, d);
  return d / g === 1 ? `${n / g}` : `${n / g}/${d / g}`;
}

function choose(n: number, r: number): number {
  let x = 1;
  for (let i = 0; i < r; i++) x = (x * (n - i)) / (i + 1);
  return Math.round(x);
}

const won = (x: number): string => `${x > 0 ? '+' : x < 0 ? '−' : ''}${Math.abs(x).toLocaleString('ko-KR')}원`;
const eunNeun = (word: string): string => `${word}${hasBatchim(word) ? '은' : '는'}`;

const TYPES: QType[] = [
  { key: 'add1', level: 1, limit: 10, make: (r) => { const a = int(r, 2, 9), b = int(r, 2, 9); return { text: `${a} + ${b}`, answer: `${a + b}` }; } },
  { key: 'sub1', level: 1, limit: 10, make: (r) => { const a = int(r, 11, 19), b = int(r, 2, 9); return { text: `${a} − ${b}`, answer: `${a - b}` }; } },
  { key: 'add2', level: 2, limit: 15, make: (r) => { const a = int(r, 12, 89), b = int(r, 12, 89); return { text: `${a} + ${b}`, answer: `${a + b}` }; } },
  { key: 'sub2', level: 2, limit: 15, make: (r) => { const a = int(r, 31, 99), b = int(r, 12, a - 5); return { text: `${a} − ${b}`, answer: `${a - b}` }; } },
  { key: 'mul1', level: 3, limit: 20, make: (r) => { const a = int(r, 12, 49), b = int(r, 3, 9); return { text: `${a} × ${b}`, answer: `${a * b}` }; } },
  { key: 'div1', level: 3, limit: 20, make: (r) => { const b = int(r, 3, 9), q = int(r, 4, 19); return { text: `${b * q} ÷ ${b}`, answer: `${q}` }; } },
  {
    key: 'pct', level: 4, limit: 20, make: (r) => {
      const p = pick(r, [10, 20, 25, 30, 40, 50, 75]);
      const unit = 100 / gcd(p, 100);
      const base = unit * int(r, 2, Math.floor(400 / unit));
      return { text: `${base}의 ${p}%는?`, answer: `${(base * p) / 100}` };
    },
  },
  {
    key: 'die1', level: 4, limit: 30, make: (r) => {
      const [label, k] = pick(r, [['짝수가', 3], ['3 이상이', 4], ['5 이상이', 2], ['6이', 1], ['1이나 2가', 2], ['4 이하가', 4]] as const);
      return { text: `주사위 하나를 던져 ${label} 나올 확률은?`, answer: frac(k, 6), wrong: [frac(k, 36), frac(Math.min(5, k + 1), 6), frac(6 - k, 6), '1/2', '1/3'] };
    },
  },
  {
    key: 'dice2', level: 5, limit: 40, make: (r) => {
      const ways = (s: number) => 6 - Math.abs(s - 7);
      if (r() < 0.6) {
        const s = int(r, 3, 11);
        return { text: `주사위 두 개를 던져 합이 ${s}일 확률은?`, answer: frac(ways(s), 36), wrong: ['1/11', frac(ways(s) + 1, 36), frac(Math.max(1, ways(s) - 1), 36), frac(ways(s), 6 * 2)] };
      }
      const s = int(r, 9, 11);
      let n = 0;
      for (let t = s; t <= 12; t++) n += ways(t);
      return { text: `주사위 두 개를 던져 합이 ${s} 이상일 확률은?`, answer: frac(n, 36), wrong: [frac(13 - s, 11), frac(n - 1, 36), frac(n + 2, 36), frac(ways(s), 36)] };
    },
  },
  {
    key: 'ncr1', level: 5, limit: 30, make: (r) => {
      const n = int(r, 4, 7), k = int(r, 2, 3);
      return { text: `${n}명 중 ${k}명을 뽑는 방법은 몇 가지? (순서 상관없음)`, answer: `${choose(n, k)}` };
    },
  },
  { key: 'mul2', level: 6, limit: 40, make: (r) => { const a = int(r, 11, 39), b = int(r, 11, 29); return { text: `${a} × ${b}`, answer: `${a * b}` }; } },
  {
    key: 'ev', level: 6, limit: 40, make: (r) => {
      if (r() < 0.5) {
        const win = pick(r, [1000, 2000, 3000, 4000, 5000]);
        const lose = pick(r, [1000, 2000, 3000, 4000, 5000].filter((x) => x !== win));
        return {
          text: `동전을 던져 앞면이면 ${win.toLocaleString('ko-KR')}원을 받고, 뒷면이면 ${lose.toLocaleString('ko-KR')}원을 낸다. 한 번 할 때 평균 이익은?`,
          answer: won((win - lose) / 2), wrong: [won(win - lose), won((win + lose) / 2), won((lose - win) / 2), won(0)],
        };
      }
      const prize = pick(r, [3000, 6000, 12000]), cost = pick(r, [300, 600, 1200]);
      return {
        text: `주사위를 던져 6이 나오면 ${prize.toLocaleString('ko-KR')}원을 받고, 아니면 ${cost.toLocaleString('ko-KR')}원을 낸다. 한 번 할 때 평균 이익은?`,
        answer: won((prize - 5 * cost) / 6), wrong: [won(prize / 6 - cost), won((prize - cost) / 6), won((prize - cost) / 2), won(-(prize - 5 * cost) / 6)],
      };
    },
  },
  {
    key: 'pctrev', level: 6, limit: 30, make: (r) => {
      const p = pick(r, [10, 20, 25, 40, 50, 75, 150]);
      const unit = 100 / gcd(p, 100);
      const y = unit * int(r, 2, Math.floor(300 / unit));
      return { text: `${eunNeun(String((y * p) / 100))} ${y}의 몇 %?`, answer: `${p}` };
    },
  },
  {
    key: 'draw2', level: 7, limit: 45, make: (r) => {
      const red = int(r, 2, 5), blue = int(r, 2, 4), n = red + blue;
      return {
        text: `주머니에 빨간 공 ${red}개, 파란 공 ${blue}개가 있다. 두 개를 꺼낼 때(다시 넣지 않음) 둘 다 빨강일 확률은?`,
        answer: frac(red * (red - 1), n * (n - 1)), wrong: [frac(red * red, n * n), frac(red, n), frac(red - 1, n - 1), frac(red * (red - 1), n * n)],
      };
    },
  },
  { key: 'ncr2', level: 7, limit: 40, make: (r) => { const n = int(r, 6, 9), k = int(r, 2, 4); return { text: `${n}개 중 ${k}개를 고르는 방법은 몇 가지? (순서 상관없음)`, answer: `${choose(n, k)}` }; } },
  {
    key: 'atleast', level: 8, limit: 45, make: (r) => pick(r, [
      { text: '주사위를 두 번 던질 때 6이 한 번 이상 나올 확률은?', answer: '11/36', wrong: ['1/3', '1/36', '25/36', '5/18'] },
      { text: '동전을 세 번 던질 때 앞면이 한 번 이상 나올 확률은?', answer: '7/8', wrong: ['3/8', '1/2', '1/8', '3/4'] },
      { text: '주사위를 두 번 던질 때 짝수가 한 번 이상 나올 확률은?', answer: '3/4', wrong: ['1/2', '1/4', '2/3', '5/6'] },
      { text: '동전을 네 번 던질 때 앞면이 한 번 이상 나올 확률은?', answer: '15/16', wrong: ['1/2', '1/4', '1/16', '7/8'] },
      { text: '주사위를 세 번 던질 때 1이 한 번도 안 나올 확률은?', answer: '125/216', wrong: ['1/2', '5/6', '1/216', '91/216'] },
    ]),
  },
  {
    key: 'pctchain', level: 8, limit: 40, make: (r) => {
      const m = int(r, 10, 99);
      const [up, down] = pick(r, [[10, 10], [20, 20], [50, 50], [10, 20], [20, 10]] as const);
      return { text: `${(m * 100).toLocaleString('ko-KR')}원이 ${up}% 오른 뒤 다시 ${down}% 내리면 몇 원?`, answer: `${(m * (100 + up) * (100 - down)) / 100}` };
    },
  },
  { key: 'mul3', level: 9, limit: 40, make: (r) => { const a = int(r, 123, 989), b = int(r, 3, 9); return { text: `${a} × ${b}`, answer: `${a * b}` }; } },
  {
    key: 'potodds', level: 9, limit: 45, make: (r) => {
      const bet = pick(r, [10, 20, 50, 100]), k = pick(r, [4, 5, 10]);
      return { text: `팟에 ${bet * (k - 2)}원이 있고 상대가 ${bet}원을 걸었다. 내가 ${bet}원을 받으려면(콜) 최소 몇 % 이길 확률이 필요할까?`, answer: `${100 / k}` };
    },
  },
  {
    key: 'bayes', level: 10, limit: 60, make: (r) => {
      const base = pick(r, [1, 2, 5, 10]), sens = pick(r, [80, 90, 95]), fpr = pick(r, [5, 10, 20]);
      const post = Math.round((100 * base * sens) / (base * sens + (100 - base) * fpr));
      return {
        text: `어떤 병에 걸린 사람은 ${base}%. 검사는 병이 있으면 ${sens}% 양성, 없어도 ${fpr}% 양성이 나온다. 양성이 나왔을 때 실제로 병이 있을 확률은 약?`,
        answer: `약 ${post}%`, wrong: [`약 ${sens}%`, `약 ${100 - fpr}%`, '약 50%', `약 ${base}%`, `약 ${Math.min(99, post * 3)}%`],
      };
    },
  },
  { key: 'sqr', level: 10, limit: 30, make: (r) => { const n = int(r, 11, 29); return { text: `${n} × ${n}`, answer: `${n * n}` }; } },
];

export function typesForLevel(level: number): QType[] {
  return TYPES.filter((t) => t.level <= level && t.level >= level - 2);
}

export function makeCalcRound(level: number, seed: number): CalcQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const pool = typesForLevel(lv);
  const out: CalcQuestion[] = [];
  let bag: QType[] = [];
  while (out.length < CALC_COUNT) {
    if (bag.length === 0) bag = shuffle(rng, pool);
    const t = bag.pop()!;
    const m = t.make(rng);
    if (out.some((q) => q.text === m.text)) continue;
    const q: CalcQuestion = { type: t.key, text: m.text, answer: m.answer, limitSec: t.limit };
    if (m.wrong) {
      const wrong = [...new Set(m.wrong.filter((w) => w !== m.answer))];
      q.choices = shuffle(rng, [m.answer, ...shuffle(rng, wrong).slice(0, 3)]);
    }
    out.push(q);
  }
  return out;
}

/** 숫자 입력 채점 — 띄어쓰기·쉼표·'원'·'%' 는 무시 */
export function checkNumber(input: string, answer: string): boolean {
  const clean = (s: string) => s.replace(/[\s,원%]/g, '').replace('−', '-');
  return clean(input) !== '' && clean(input) === clean(answer);
}

/** 숫자 하나로 읽기 — 쉼표·단위 글자(원·만·명…)는 무시, 못 읽으면 null */
export function parseNumber(input: string): number | null {
  const s = input.replace(/[−–]/g, '-').replace(/[^0-9.\-]/g, '');
  return /^-?(\d+(\.\d*)?|\.\d+)$/.test(s) ? Number(s) : null;
}

/** 허용 오차 안이면 정답 — tol = 비율(0.05 = ±5%), tolAbs = 절대값, 둘 중 큰 쪽. 둘 다 없으면 정확히 같아야 */
export function checkApprox(input: string, answer: number, tol = 0, tolAbs = 0): boolean {
  const x = parseNumber(input);
  return x !== null && Math.abs(x - answer) <= Math.max(tolAbs, tol * Math.abs(answer)) + 1e-9;
}
